import { useSuspenseQuery } from '@apollo/client/react';
import { useNavigate } from 'react-router';

import {
  SoftwareOnboardingLayout,
  SoftwareOnboardingHeader,
  SoftwareOnboardingMain,
  SoftwareOnboardingFooter,
} from 'src/components/SoftwareOnboarding';
import { ExitAlertDialog } from 'src/pages/SoftwareEscrowRequestChanges/ui';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { DepositsSuggestChangesContextProvider } from 'src/context/suggest-changes/DepositsSuggestChangesContext';
import { AssetContentTypeEnum } from 'src/graphql/schema';
import { AssetsDocument } from 'src/graphql/actions/asset/assets.query';

import { SuggestChangesAssetsBlock, SubmitChangesDialog } from './ui';

const Content = () => {
  const {
    softwareEscrow: { id: softwareEscrowId },
  } = useSoftwareEscrow();
  const {
    data: { assets },
  } = useSuspenseQuery(AssetsDocument, {
    variables: {
      softwareEscrowId,
    },
  });
  const navigate = useNavigate();

  const onDiscardChanges = () => navigate(`/software-escrows/${softwareEscrowId}/deposits`);

  return (
    <SoftwareOnboardingLayout>
      <SoftwareOnboardingHeader justify="end">
        <ExitAlertDialog onDiscard={onDiscardChanges} />
      </SoftwareOnboardingHeader>
      <SoftwareOnboardingMain
        subtitle="Request changes to:"
        title="Deposits"
        noteText="You can request the removal of assets by clicking on the ‘delete’ icon."
        classes={{
          note: 'not-italic [&>span]:hidden',
          subtitle: 'font-medium',
        }}
      >
        <div className="container mx-auto space-y-6 lg:p-6">
          <SuggestChangesAssetsBlock
            title="Unlabeled assets"
            description="The assets listed below are connected to your escrow but don’t have an assigned asset type. Please review and label each asset appropriately."
            totalAssets={assets.nodesCount}
            contentType="unlabeled"
          />
          <SuggestChangesAssetsBlock
            title="Source code"
            description="The core source code, including all scripts, libraries, and dependencies required to build and run the application."
            contentType={AssetContentTypeEnum.SourceCode}
            totalAssets={assets.nodesCount}
          />
          <SuggestChangesAssetsBlock
            title="Documentation"
            description="Technical and operational guides, including API references, setup instructions, system architecture, and any relevant notes."
            contentType={AssetContentTypeEnum.Documentation}
            totalAssets={assets.nodesCount}
          />
          <SuggestChangesAssetsBlock
            title="Data"
            description="Essential datasets, configurations, and database exports necessary for the application’s functionality."
            contentType={AssetContentTypeEnum.Data}
            totalAssets={assets.nodesCount}
          />
        </div>
      </SoftwareOnboardingMain>
      <SoftwareOnboardingFooter justify="end">
        <SubmitChangesDialog />
      </SoftwareOnboardingFooter>
    </SoftwareOnboardingLayout>
  );
};

const SoftwareEscrowRequestChangesDeposits = () => {
  const {
    softwareEscrow: { id },
  } = useSoftwareEscrow();

  return (
    <DepositsSuggestChangesContextProvider escrowId={id}>
      <Content />
    </DepositsSuggestChangesContextProvider>
  );
};

export default SoftwareEscrowRequestChangesDeposits;
